import { memo } from 'react';
import { AppealStats } from '../../types/appeals';
import { Clock, CheckCircle, XCircle, AlertTriangle, Users, Gavel, Hourglass, TrendingUp, Bot, FileText, Scale } from 'lucide-react';
import { V2_OUTCOME_META, V2Outcome } from '../../utils/appealsV2';
import { cn } from '../../utils/cn';
import { StatTile } from '../ui/StatTile';
import { SectionHeader } from '../ui/SectionHeader';

interface AppealStatsCardsProps {
  stats: AppealStats | null;
  isLoading: boolean;
}

type StatTone = 'neutral' | 'accent' | 'purple' | 'success' | 'warning' | 'amber' | 'orange' | 'danger' | 'info';

const StatCard = memo(({ icon, label, value, subValue, tone }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  subValue?: string;
  tone: StatTone;
}) => (
  <StatTile icon={icon} label={label} value={value} hint={subValue} tone={tone} />
));

const formatDuration = (ms: number | null): string => {
  if (ms === null) return 'N/A';
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  return `${hours}h`;
};

export const AppealStatsCards = memo(({ stats, isLoading }: AppealStatsCardsProps) => {
  if (isLoading || !stats) {
    return (
      <div className="space-y-4" aria-busy="true">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <StatTile key={i} label="" value="" loading />
          ))}
        </div>
      </div>
    );
  }

  const staffAnsweredCount = stats.by_status.staff_answered || 0;
  const juryDecidedCount = stats.by_status.jury_decided || 0;
  const escalatedCount = stats.by_status.escalated || 0;

  // Jury V2 buckets — only rendered when the backend provides them, so the
  // dashboard is unchanged against older responses.
  const hasVersionSplit = !!stats.by_version && stats.total > 0;
  const v2AdoptionPct = hasVersionSplit ? Math.round((stats.by_version!.v2 / stats.total) * 100) : 0;
  const hasStageCounts = stats.in_jury_stage_1_count !== undefined || stats.in_jury_stage_2_count !== undefined;
  const v2Outcomes = stats.by_v2_outcome;
  const v2OutcomeTotal = v2Outcomes
    ? Object.values(v2Outcomes).reduce((sum, n) => sum + (n || 0), 0)
    : 0;
  const V2_OUTCOME_ORDER: V2Outcome[] = ['upheld', 'removed', 'reduced', 'increased', 'made_unappealable'];

  return (
    <div className="space-y-4">
      {/* Pipeline overview */}
      <div className="glass-card p-4 md:p-5">
        <SectionHeader
          title="Appeal Pipeline"
          icon={TrendingUp}
          count={`${stats.total} total`}
          actions={hasVersionSplit && stats.by_version!.v2 > 0 ? (
            <span
              className="badge badge-purple tabular-nums"
              title={`${stats.by_version!.v2} of ${stats.total} appeals use the V2 two-stage jury`}
            >
              V2 adoption: {v2AdoptionPct}%
            </span>
          ) : undefined}
        />
        <div className="flex flex-col sm:flex-row gap-2">
          {/* Staff-actionable */}
          <div className="flex-1 rounded-lg border border-orange-500/20 bg-orange-500/5 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <FileText className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-xs font-semibold text-orange-300">Staff Action</span>
            </div>
            <div className="flex items-center gap-4">
              <div>
                <p className="text-xl font-semibold tracking-tight tabular-nums text-white">{stats.pending_count}</p>
                <p className="text-xs text-glass-400">Pending</p>
              </div>
              <div className="w-px h-8 bg-glass-200/10" />
              <div>
                <p className="text-xl font-semibold tracking-tight tabular-nums text-white">{stats.awaiting_staff_count}</p>
                <p className="text-xs text-glass-400">Awaiting Answers</p>
              </div>
            </div>
          </div>

          <div className="hidden sm:flex items-center">
            <div className="w-4 h-px bg-glass-200/20" />
            <div className="w-0 h-0 border-l-4 border-l-glass-200/20 border-y-4 border-y-transparent" />
          </div>

          {/* Mr.Roboto-managed */}
          <div className="flex-1 rounded-lg border border-purple-500/20 bg-purple-500/5 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Bot className="w-3.5 h-3.5 text-purple-400" />
              <span className="text-xs font-semibold text-purple-300">Mr.Roboto / Jury</span>
            </div>
            <div className="flex items-center gap-4">
              <div>
                <p className="text-xl font-semibold tracking-tight tabular-nums text-white">{staffAnsweredCount}</p>
                <p className="text-xs text-glass-400">Mr.Roboto Queue</p>
              </div>
              <div className="w-px h-8 bg-glass-200/10" />
              <div>
                <p className="text-xl font-semibold tracking-tight tabular-nums text-white">{stats.in_jury_count}</p>
                <p className="text-xs text-glass-400">In Jury</p>
                {hasStageCounts && ((stats.in_jury_stage_1_count || 0) > 0 || (stats.in_jury_stage_2_count || 0) > 0) && (
                  <p className="text-xs text-indigo-300 mt-0.5 tabular-nums" title="V2 two-stage juries currently voting">
                    S1: {stats.in_jury_stage_1_count || 0} · S2: {stats.in_jury_stage_2_count || 0}
                  </p>
                )}
              </div>
              <div className="w-px h-8 bg-glass-200/10" />
              <div>
                <p className="text-xl font-semibold tracking-tight tabular-nums text-white">{juryDecidedCount}</p>
                <p className="text-xs text-glass-400">Decided</p>
              </div>
            </div>
          </div>

          <div className="hidden sm:flex items-center">
            <div className="w-4 h-px bg-glass-200/20" />
            <div className="w-0 h-0 border-l-4 border-l-glass-200/20 border-y-4 border-y-transparent" />
          </div>

          {/* Outcomes */}
          <div className="flex-1 subcard p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Gavel className="w-3.5 h-3.5 text-glass-400" />
              <span className="text-xs font-semibold text-glass-300">Outcomes</span>
            </div>
            <div className="flex items-center gap-4">
              <div>
                <p className="text-xl font-semibold tracking-tight tabular-nums text-green-400">{stats.by_status.approved || 0}</p>
                <p className="text-xs text-glass-400">Approved</p>
              </div>
              <div className="w-px h-8 bg-glass-200/10" />
              <div>
                <p className="text-xl font-semibold tracking-tight tabular-nums text-red-400">{stats.by_status.denied || 0}</p>
                <p className="text-xs text-glass-400">Denied</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Jury V2 outcome distribution — only when the backend reports V2 outcomes */}
      {v2Outcomes && v2OutcomeTotal > 0 && (
        <div className="glass-card p-4 md:p-5">
          <SectionHeader title="V2 Jury Outcomes" icon={Scale} iconClassName="text-indigo-400" count={`${v2OutcomeTotal} decided`} />
          <div className="space-y-2">
            {V2_OUTCOME_ORDER.map(outcome => {
              const meta = V2_OUTCOME_META[outcome];
              const count = v2Outcomes[outcome] || 0;
              const pct = v2OutcomeTotal > 0 ? (count / v2OutcomeTotal) * 100 : 0;
              return (
                <div key={outcome} className="flex items-center gap-3">
                  <span className={cn('w-28 flex-shrink-0 text-xs font-medium', meta.text)}>
                    {meta.label}
                  </span>
                  <div className="flex-1 bg-glass-100/10 rounded-full h-2.5 overflow-hidden">
                    <div className={cn('h-full rounded-full', meta.bar)} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-14 flex-shrink-0 text-right text-xs text-glass-400 tabular-nums">
                    {count} <span className="text-glass-500">({Math.round(pct)}%)</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Detail stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={Hourglass}
          label="Pending"
          value={stats.pending_count}
          subValue="Awaiting assignment"
          tone="warning"
        />
        <StatCard
          icon={AlertTriangle}
          label="Awaiting Staff"
          value={stats.awaiting_staff_count}
          subValue="Need staff answers"
          tone="orange"
        />
        <StatCard
          icon={Bot}
          label="With Mr.Roboto"
          value={staffAnsweredCount + stats.in_jury_count + juryDecidedCount}
          subValue="Managed by Mr.Roboto"
          tone="purple"
        />
        <StatCard
          icon={TrendingUp}
          label="Approval Rate"
          value={stats.approval_rate !== null ? `${stats.approval_rate.toFixed(1)}%` : 'N/A'}
          subValue={`Avg. ${formatDuration(stats.avg_decision_time_ms)} to decision`}
          tone="success"
        />
        <StatCard
          icon={Users}
          label="Total Appeals"
          value={stats.total}
          tone="info"
        />
        <StatCard
          icon={CheckCircle}
          label="Approved"
          value={stats.by_status.approved || 0}
          tone="success"
        />
        <StatCard
          icon={XCircle}
          label="Denied"
          value={stats.by_status.denied || 0}
          tone="danger"
        />
        <StatCard
          icon={Clock}
          label="Escalated / Cancelled"
          value={escalatedCount + (stats.by_status.cancelled || 0)}
          subValue={`${escalatedCount} escalated, ${stats.by_status.cancelled || 0} cancelled`}
          tone="amber"
        />
      </div>
    </div>
  );
});
